/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { MetadataApiService } from './metadata-api.service';
import { QuantApiService } from '../quant-api/quant-api.service';
import { ConfigService } from 'src/app/service/config.service';
import { Entity } from '../../models/entity.interface';
import { ParamsBuilder } from '../../utils/params-builder';
import { UserStateService } from '../user-state.service';

export interface GroupingEntity {
  type: string;
  alpha_id: string;
  compound_key: string;
  name: string | null;
}

export interface GroupingInfoResponse {
  entities?: GroupingEntity[];
  entity_types?: GroupingEntity[];
}

export interface SanctionsResponse {
  sanctions: Array<{
    sanctioned_entity: { compound_key: string };
    [key: string]: any;
  }>;
}

@Injectable({
  providedIn: 'root',
})
export class EntityInfoService {
  // Two independent caches: `entity_info` POST is keyed only by the sorted
  // compound_keys, and `sanctions/entity` POST (same keys) lives in its own
  // cache. A `sanctions=false` caller and a `sanctions=true` caller with the
  // same entities thus share one entity_info HTTP — only the sanctions
  // fetch + merge is conditional.
  private entityInfoOnlyCache = new Map<string, Observable<Entity[]>>();
  private sanctionsCache = new Map<string, Observable<SanctionsResponse>>();
  private hierarchyCache = new Map<string, Observable<any>>();

  constructor(
    private metadataApi: MetadataApiService,
    private quantApi: QuantApiService,
    private config: ConfigService,
    private userState: UserStateService,
  ) {}

  // Get detailed info about entities based on compound_key values.
  // Returns an object keyed by compound_key (default) or an array.
  // The sanctions HTTP is skipped whenever the user's `showSanctions`
  // feature flag is off, since every UI consumer of `entity.sanctions`
  // is already gated on the same flag.
  getEntitiesInfo(
    entitiesArray: string[],
    format: 'object' | 'array' = 'object',
    sanctions = false,
  ): Observable<Record<string, Entity> | Entity[]> {
    const key = [...entitiesArray].sort().join('|');
    const wantsSanctions =
      sanctions && this.userState.featureFlags?.showSanctions !== false;
    return forkJoin([
      this.fetchEntityInfoOnly(entitiesArray, key),
      wantsSanctions
        ? this.fetchSanctions(entitiesArray, key)
        : of({ sanctions: [] } as SanctionsResponse),
    ]).pipe(
      map(([rawEntities, sanctionsResponse]) => {
        const entities: any[] = JSON.parse(JSON.stringify(rawEntities));
        const sanctionsData = sanctionsResponse.sanctions ?? [];
        entities.forEach((entity, index) => {
          entities[index]['sanctions'] = sanctionsData.filter(
            (s: any) =>
              s.sanctioned_entity.compound_key === entity.compound_key,
          );
        });
        return format === 'object'
          ? this.toMap(entities as Entity[])
          : (entities as Entity[]);
      }),
    );
  }

  private fetchEntityInfoOnly(
    entitiesArray: string[],
    key: string,
  ): Observable<Entity[]> {
    const cached = this.entityInfoOnlyCache.get(key);
    if (cached) return cached;
    const result$ = this.metadataApi
      .post<Entity[]>('entity_info', entitiesArray)
      .pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
        catchError((err) => {
          this.entityInfoOnlyCache.delete(key);
          return throwError(() => err);
        }),
      );
    this.entityInfoOnlyCache.set(key, result$);
    return result$;
  }

  private fetchSanctions(
    entitiesArray: string[],
    key: string,
  ): Observable<SanctionsResponse> {
    const cached = this.sanctionsCache.get(key);
    if (cached) return cached;
    const result$ = this.getSanctionsInfo(entitiesArray).pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError(() => {
        this.sanctionsCache.delete(key);
        return of({ sanctions: [] } as SanctionsResponse);
      }),
    );
    this.sanctionsCache.set(key, result$);
    return result$;
  }

  private toMap(entities: Entity[]): Record<string, Entity> {
    const result: Record<string, Entity> = {};
    entities.forEach((el) => {
      result[el.compound_key] = el;
    });
    return result;
  }

  clearCache(): void {
    this.entityInfoOnlyCache.clear();
    this.sanctionsCache.clear();
    this.hierarchyCache.clear();
  }

  /**
   * Get the name of a single entity.
   */
  getEntityName(requestedEntity: string[]): Observable<string> {
    return this.metadataApi.post<Entity[]>('entity_info', requestedEntity).pipe(
      map((res) => {
        if (!res || !Array.isArray(res) || res.length === 0 || !res[0]?.name) {
          return '';
        }
        return res[0].name;
      }),
    );
  }

  /**
   * Get sanctions data for entities.
   */
  getSanctionsInfo(entitiesArray: string[]): Observable<SanctionsResponse> {
    return this.metadataApi
      .post<SanctionsResponse>('sanctions/entity', entitiesArray)
      .pipe(
        catchError((error) => {
          console.log(error);
          return of({ sanctions: [] } as SanctionsResponse);
        }),
      );
  }

  /**
   * Get related entities info filtered by entity type.
   */
  getRelatedEntitiesInfo(
    type: string,
    id: string,
    filter: string,
  ): Observable<any> {
    return this.metadataApi.get(`related/${type}.${id}`, {
      entity_type_filter: filter,
    });
  }

  /**
   * Get top related entities with parent entity info.
   */
  getTopRelatedEntities(
    type: string,
    id: string,
    size: number,
  ): Observable<any> {
    const parentEntity = `${type}:${id}`;
    return forkJoin([
      this.getEntitiesInfo([parentEntity]),
      this.metadataApi.get(`approximation/${type}.${id}`, {
        limit: String(size),
      }),
    ]).pipe(
      map(([entityInfo, approximation]: [any, any]) => {
        return { ...approximation, entity: entityInfo[parentEntity] };
      }),
    );
  }

  /**
   * Parse ISIN codes to entities.
   */
  isinParse(isins: string[]): Observable<any> {
    return this.metadataApi.post('isin_to_entity', isins);
  }

  /**
   * Get supply chain supplier info.
   */
  getSupplyChainInfo(
    entityName: string,
    size: number = 100,
  ): Observable<any[]> {
    return this.metadataApi
      .get<any>(`supply_chain/suppliers/${entityName}`, {
        limit: String(size),
      })
      .pipe(
        map((res) => res.suppliers || []),
        catchError((error) => throwError(() => error)),
      );
  }

  /**
   * Get hierarchy info.
   * ICB/event types use metadata API, industry/country/index use quant API.
   */
  getHierarchyInfo(
    type: string,
    iparams: Record<string, any>,
  ): Observable<any> {
    const cached = this.hierarchyCache.get(type);
    if (cached) return cached;
    const paramsToBeSent = [
      'period',
      'with_top_events',
      'with_all_events',
      ...ParamsBuilder.PARAMS_FOR_ALL_REQUESTS,
    ];
    const params = ParamsBuilder.build(
      iparams,
      paramsToBeSent,
      this.config.appConfig.routing.apiproj,
    );

    const source$ = ['icb', 'event'].includes(type)
      ? this.metadataApi.get(`hierarchy/${type}`, params)
      : this.quantApi.get('signal/market_board', {
          ...params,
          board_type: type,
        });
    const result$ = source$.pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError((err) => {
        this.hierarchyCache.delete(type);
        return throwError(() => err);
      }),
    );
    this.hierarchyCache.set(type, result$);
    return result$;
  }

  // --- Reference Data ---

  getEntityTypes(): Observable<GroupingInfoResponse> {
    return this.metadataApi.get<GroupingInfoResponse>(
      'grouping_info/entity_types',
      null,
    );
  }

  getIndustries(): Observable<GroupingInfoResponse> {
    return this.metadataApi.get<GroupingInfoResponse>(
      'grouping_info/industries',
      null,
    );
  }

  getSubsectors(): Observable<GroupingInfoResponse> {
    return this.metadataApi.get<GroupingInfoResponse>(
      'grouping_info/subsectors',
      null,
    );
  }

  getSectors(): Observable<GroupingInfoResponse> {
    return this.metadataApi.get<GroupingInfoResponse>(
      'grouping_info/sectors',
      null,
    );
  }

  getCountries(): Observable<GroupingInfoResponse> {
    return this.metadataApi.get<GroupingInfoResponse>(
      'grouping_info/countries',
      null,
    );
  }

  getContinents(): Observable<GroupingInfoResponse> {
    return this.metadataApi.get<GroupingInfoResponse>(
      'grouping_info/continents',
      null,
    );
  }

  getIndexes(): Observable<any> {
    return this.quantApi.get('misc/supported_indexes', null);
  }

  getTrendingCountries(): Observable<any> {
    return this.quantApi.get('misc/supported_trending_countries', null);
  }
}
